/**
 * SupplierRepository — who goods are bought from.
 *
 *   GET    /stock/suppliers?q=   list, optional prefix search on the name
 *   POST   /stock/suppliers      create
 *   PUT    /stock/suppliers      merge-patch
 *   DELETE /stock/suppliers      soft delete (deactivate)
 *
 * This replaces the stub that read a legacy SupplierTable keyed by `salonId`
 * from an older product — a table with no write API, whose one read route was
 * commented out of serverless.yml. Nothing is migrated from it.
 *
 * See docs/feature-stock-intake-and-suppliers.md §5.1 and §7.3.
 */
import {posApiRequest} from './posApi';

export const PAYMENT_TERMS = [
  {value: 'cash', label: 'Cash'},
  {value: 'credit_7', label: '7 days credit'},
  {value: 'credit_30', label: '30 days credit'},
  {value: 'other', label: 'Other'},
];

export const paymentTermLabel = (value?: string | null): string =>
  PAYMENT_TERMS.find((t) => t.value === value)?.label ?? '—';

export interface Supplier {
  supplierId: string;
  name: string;
  nameLower?: string;
  contactName?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  paymentTerms?: string | null;
  notes?: string | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  /** Computed by the server on list, from posted intakes only. */
  lastIntakeAt?: string | null;
  intakeCount?: number;
  receivedTotal30d?: number;
}

export interface SupplierInput {
  name: string;
  contactName?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  paymentTerms?: string | null;
  notes?: string | null;
}

const SupplierRepository = {
  async list(shopId: string, q?: string, includeInactive = false): Promise<Supplier[]> {
    const params = [`merchantId=${encodeURIComponent(shopId)}`];
    if (q) params.push(`q=${encodeURIComponent(q)}`);
    if (includeInactive) params.push('includeInactive=true');
    const res = await posApiRequest(`/stock/suppliers?${params.join('&')}`);
    return res?.suppliers ?? [];
  },

  async create(shopId: string, supplier: SupplierInput): Promise<Supplier> {
    const res = await posApiRequest('/stock/suppliers', {
      method: 'POST',
      body: JSON.stringify({merchantId: shopId, supplier}),
    });
    return res?.supplier;
  },

  async update(
    shopId: string,
    supplier: Partial<Supplier> & {supplierId: string},
  ): Promise<Supplier> {
    const res = await posApiRequest('/stock/suppliers', {
      method: 'PUT',
      body: JSON.stringify({merchantId: shopId, supplier}),
    });
    return res?.supplier;
  },

  /** Deactivate, so a posted intake's supplier reference still resolves. */
  async deactivate(shopId: string, supplierId: string): Promise<Supplier> {
    const res = await posApiRequest(
      `/stock/suppliers?merchantId=${encodeURIComponent(shopId)}&supplierId=${encodeURIComponent(supplierId)}`,
      {method: 'DELETE'},
    );
    return res?.supplier;
  },
};

export default SupplierRepository;
